import { ANALYTICS_CONFIG } from './config';
import { insertEvents, upsertSession, updateSession } from './supabase';

// URL de l'API Supabase pour sendBeacon
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

class EventQueue {
  constructor() {
    this.queue = [];
    this.isFlushing = false;
    this.flushTimer = null;
    this.currentSessionId = null;

    // Démarrer le flush périodique
    this._startPeriodicFlush();

    // Flush sur fermeture de page avec sendBeacon
    window.addEventListener('beforeunload', () => this.flushWithBeacon());
    window.addEventListener('pagehide', () => this.flushWithBeacon());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushWithBeacon();
      }
    });
  }

  setSessionId(sessionId) {
    this.currentSessionId = sessionId;
  }

  add(event) {
    this.queue.push(event);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Event queued:', event.event, event);
    }

    // Flush si batch size atteint
    if (this.queue.length >= ANALYTICS_CONFIG.batchSize) {
      this.flush();
    }
  }

  async flush(immediate = false) {
    if (this.queue.length === 0) return;
    if (this.isFlushing && !immediate) return;

    const events = [...this.queue];
    this.queue = [];
    this.isFlushing = true;

    try {
      await this._sendToSupabase(events);
    } catch (error) {
      // Remettre en queue en cas d'erreur (avec limite pour éviter memory leak)
      this.queue = [...events, ...this.queue];

      // Limite à 500 événements max pour éviter les memory leaks
      if (this.queue.length > 500) {
        this.queue = this.queue.slice(-100); // Garder les 100 derniers
        console.warn('[Analytics] Queue overflow, dropping old events');
      }

      if (ANALYTICS_CONFIG.debug) {
        console.warn('[Analytics] Error sending to Supabase, events re-queued:', error);
      }
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Flush synchrone avec sendBeacon pour les fermetures de page
   * sendBeacon garantit l'envoi même si la page se ferme
   */
  flushWithBeacon() {
    if (this.queue.length === 0) return;
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;

    const events = [...this.queue];
    this.queue = [];

    // Transformer les événements au format Supabase
    // Copier TOUS les champs sauf les métadonnées de base
    const supabaseEvents = events.map(event => {
      const { event_id, session_id, timestamp, event: eventType, ...eventData } = event;
      return {
        session_id: session_id || this.currentSessionId,
        event_type: eventType,
        event_data: eventData,
        timestamp: timestamp || new Date().toISOString()
      };
    });

    // Utiliser sendBeacon pour garantir l'envoi
    const url = `${SUPABASE_URL}/rest/v1/analytics_events`;
    const blob = new Blob([JSON.stringify(supabaseEvents)], { type: 'application/json' });

    // sendBeacon avec headers via fetch keepalive en fallback
    try {
      const success = navigator.sendBeacon(url + `?apikey=${SUPABASE_ANON_KEY}`, blob);

      if (!success) {
        // Fallback: fetch avec keepalive
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify(supabaseEvents),
          keepalive: true
        }).catch(() => {});
      }

      if (ANALYTICS_CONFIG.debug) {
        console.log('[Analytics] Beacon sent:', supabaseEvents.length, 'events, success:', success);
      }
    } catch (error) {
      if (ANALYTICS_CONFIG.debug) {
        console.warn('[Analytics] Beacon error:', error);
      }
    }
  }

  async _sendToSupabase(events) {
    if (events.length === 0) return;

    // Transformer les événements au format Supabase
    // Copier TOUS les champs sauf les métadonnées de base
    const supabaseEvents = events.map(event => {
      const { event_id, session_id, timestamp, event: eventType, ...eventData } = event;
      return {
        session_id: session_id || this.currentSessionId,
        event_type: eventType,
        event_data: eventData,
        timestamp: timestamp || new Date().toISOString()
      };
    });

    const result = await insertEvents(supabaseEvents);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[Analytics] Sent to Supabase:', result?.length || 0, 'events');
    }

    return result;
  }

  _startPeriodicFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, ANALYTICS_CONFIG.flushIntervalMs);
  }

  clear() {
    this.queue = [];
  }

  getQueueLength() {
    return this.queue.length;
  }

  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush(true);
  }
}

// Export aussi les fonctions de session pour usage externe
export { upsertSession, updateSession };

export default EventQueue;
